import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatRupees, formatRupeesCompact, todayISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Save, RefreshCw, TrendingUp, TrendingDown, Sparkles } from "lucide-react";

const TYPE_LABEL = {
  sip: "SIP", lumpsum: "Lumpsum / Stocks", sebi_startup_fund: "AIF / Startup Fund",
};
const TYPE_PILL = {
  sip: "bg-indigo-50 text-indigo-700 border-indigo-200",
  lumpsum: "bg-emerald-50 text-emerald-700 border-emerald-200",
  sebi_startup_fund: "bg-amber-50 text-amber-700 border-amber-200",
};

const daysAgo = (iso) => {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00");
  const now = new Date();
  return Math.max(0, Math.round((now - d) / 86400000));
};

const stalenessPill = (days) => {
  if (days === null) return { text: "Never", cls: "bg-red-50 text-red-700 border-red-200" };
  if (days === 0) return { text: "Today", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  if (days <= 7) return { text: `${days}d ago`, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  if (days <= 30) return { text: `${days}d ago`, cls: "bg-amber-50 text-amber-700 border-amber-200" };
  return { text: `${days}d stale`, cls: "bg-red-50 text-red-700 border-red-200" };
};

export default function BulkNav() {
  const [rows, setRows] = useState([]);
  const [asOf, setAsOf] = useState(todayISO());
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inputs, setInputs] = useState({}); // { [id]: { units, nav } }
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/valuations/bulk-nav-candidates");
      setRows(r.data);
      const seed = {};
      for (const row of r.data) {
        seed[row.id] = { units: row.units_held ? String(row.units_held) : "", nav: "" };
      }
      setInputs(seed);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load investments");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const setInput = (id, patch) => setInputs((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    return rows.filter((r) => r.type === filter);
  }, [rows, filter]);

  const preview = useMemo(() => {
    let filledCount = 0, filledValue = 0, oldValue = 0, gain = 0;
    for (const r of filtered) {
      const inp = inputs[r.id] || {};
      const nav = Number(inp.nav || 0);
      const units = Number(inp.units || r.units_held || 0);
      if (nav > 0) {
        const nv = units * nav;
        filledCount += 1;
        filledValue += nv;
        oldValue += Number(r.last_snapshot_value || r.current_value || 0);
        gain += nv - Number(r.last_snapshot_value || r.current_value || 0);
      }
    }
    return { filledCount, filledValue, oldValue, gain };
  }, [filtered, inputs]);

  const applyLast = () => {
    // Fill NAV with last snapshot's NAV where units are known (helps quick review)
    const next = { ...inputs };
    for (const r of rows) {
      if (!next[r.id]) next[r.id] = {};
      if (!next[r.id].nav && r.latest_nav) next[r.id].nav = String(r.latest_nav);
      if (!next[r.id].units && r.units_held) next[r.id].units = String(r.units_held);
    }
    setInputs(next);
    toast.success("Filled from last NAV — review and update before saving");
  };

  const clearAll = () => {
    const next = {};
    for (const r of rows) next[r.id] = { units: r.units_held ? String(r.units_held) : "", nav: "" };
    setInputs(next);
  };

  const save = async () => {
    const payloadRows = rows
      .map((r) => {
        const inp = inputs[r.id] || {};
        const nav = Number(inp.nav || 0);
        if (nav <= 0) return null;
        const units = Number(inp.units || r.units_held || 0);
        return { investment_id: r.id, nav_per_unit: nav, units };
      })
      .filter(Boolean);
    if (payloadRows.length === 0) return toast.error("Enter a NAV for at least one investment");
    setSaving(true);
    try {
      const r = await api.post("/valuations/bulk-nav", { as_of_date: asOf, notes, rows: payloadRows });
      toast.success(`Updated ${r.data.count} investment${r.data.count === 1 ? "" : "s"} · snapshots saved`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const counts = useMemo(() => {
    const c = { all: rows.length, sip: 0, lumpsum: 0, sebi_startup_fund: 0 };
    for (const r of rows) c[r.type] = (c[r.type] || 0) + 1;
    return c;
  }, [rows]);

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <Link to="/market-valuation" className="inline-flex items-center gap-2 text-[12px] text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Market Valuation
      </Link>

      <div>
        <div className="eyebrow flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-red-600" />
          MONTHLY RITUAL
        </div>
        <h1 className="mt-2 font-editorial font-light text-[38px] leading-[1.02] sm:text-5xl md:text-6xl text-slate-900 tracking-[-0.035em]" data-testid="bulk-nav-title">
          Bulk <em className="italic">NAV refresh.</em>
        </h1>
        <p className="mt-2 text-sm text-slate-500 max-w-2xl">
          Paste today&apos;s NAV for every mutual fund, stock lot and AIF unit in one go — one save, all portfolio values, XIRRs and dashboards refresh instantly.
        </p>
      </div>

      {/* Ribbon: date + type filter + preview totals */}
      <div className="border border-slate-200 bg-white rounded-md p-4 sm:p-5 space-y-4">
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label>As-of date <span className="text-red-600">*</span></Label>
            <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="mt-1.5" data-testid="bn-asof" />
          </div>
          <div className="sm:col-span-2">
            <Label>Note <span className="text-slate-400 text-[10px]">(optional — same on all snapshots)</span></Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Feb 2026 month-end NAV refresh" className="mt-1.5" data-testid="bn-note" />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
          {[
            { k: "all", label: "All", n: counts.all },
            { k: "sip", label: "SIP", n: counts.sip },
            { k: "lumpsum", label: "Lumpsum", n: counts.lumpsum },
            { k: "sebi_startup_fund", label: "AIF", n: counts.sebi_startup_fund },
          ].map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)}
              className={`text-[12px] px-3 py-1.5 rounded-md border transition-colors ${
                filter === f.k
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
              data-testid={`bn-filter-${f.k}`}>
              {f.label} <span className="ml-1 text-[10px] opacity-70 font-mono-fin">{f.n}</span>
            </button>
          ))}
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={applyLast} className="rounded-md h-8 text-[11px] gap-1" data-testid="bn-fill-last">
            <RefreshCw className="h-3 w-3" /> Fill from last NAV
          </Button>
          <Button variant="outline" size="sm" onClick={clearAll} className="rounded-md h-8 text-[11px]" data-testid="bn-clear">
            Clear
          </Button>
        </div>

        {/* Live preview */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 border-t border-slate-100 pt-3">
          <div className="p-3 rounded-md bg-slate-50 border border-slate-200 flex items-baseline sm:block gap-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 whitespace-nowrap">Rows filled</div>
            <div className="sm:mt-1 font-mono-fin text-lg font-semibold text-slate-900 tabular-nums" data-testid="bn-count">
              {preview.filledCount} <span className="text-[11px] text-slate-400 font-normal">/ {filtered.length}</span>
            </div>
          </div>
          <div className="p-3 rounded-md bg-white border border-slate-200 flex items-baseline sm:block gap-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 whitespace-nowrap">New portfolio value</div>
            <div className="sm:mt-1 font-mono-fin text-lg font-semibold text-slate-900 tabular-nums" data-testid="bn-new-value">
              {formatRupeesCompact(preview.filledValue)}
            </div>
          </div>
          <div className={`p-3 rounded-md border flex items-baseline sm:block gap-3 ${preview.gain >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1 whitespace-nowrap">
              {preview.gain >= 0 ? <TrendingUp className="h-3 w-3 text-emerald-700" /> : <TrendingDown className="h-3 w-3 text-red-700" />}
              Δ vs last
            </div>
            <div className={`sm:mt-1 font-mono-fin text-lg font-semibold tabular-nums ${preview.gain >= 0 ? "text-emerald-700" : "text-red-700"}`}>
              {preview.gain >= 0 ? "+" : ""}{formatRupeesCompact(preview.gain)}
            </div>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="border border-slate-200 bg-white rounded-md overflow-hidden">
        <div className="hidden md:grid grid-cols-[minmax(0,1fr)_100px_140px_140px_150px_150px] gap-3 px-4 py-2 border-b border-slate-200 bg-slate-50 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
          <div>Investment</div>
          <div className="text-right">Type</div>
          <div className="text-right">Units</div>
          <div className="text-right">New NAV (Rs)</div>
          <div className="text-right">Preview value</div>
          <div className="text-right">Last update</div>
        </div>

        {busy && (
          <div className="p-8 text-center text-[12px] text-slate-500">Loading investments…</div>
        )}
        {!busy && filtered.length === 0 && (
          <div className="p-8 text-center text-[13px] text-slate-500">
            No SIP / Lumpsum / AIF investments found. Add one from <Link to="/investments/new" className="text-red-600 hover:underline">Investments → Add</Link> to start updating NAVs.
          </div>
        )}

        <div className="divide-y divide-slate-100">
          {filtered.map((r) => {
            const inp = inputs[r.id] || {};
            const units = Number(inp.units || r.units_held || 0);
            const nav = Number(inp.nav || 0);
            const preview = nav > 0 ? units * nav : 0;
            const lastVal = Number(r.last_snapshot_value || r.current_value || 0);
            const delta = preview - lastVal;
            const stale = stalenessPill(daysAgo(r.last_snapshot_date));
            return (
              <div key={r.id} className="px-4 py-3 hover:bg-slate-50/60" data-testid={`bn-row-${r.id}`}>
                <div className="md:grid md:grid-cols-[minmax(0,1fr)_100px_140px_140px_150px_150px] md:gap-3 md:items-center space-y-2 md:space-y-0">
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium text-slate-900 truncate">{r.name}</div>
                    <div className="text-[10.5px] text-slate-500 truncate font-mono-fin">
                      {r.code || "—"} · Invested {formatRupeesCompact(r.amount_invested)}
                    </div>
                  </div>
                  <div className="md:text-right">
                    <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded-sm border ${TYPE_PILL[r.type] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                      {TYPE_LABEL[r.type] || r.type}
                    </span>
                  </div>
                  <div>
                    <div className="md:hidden text-[10px] uppercase tracking-wider text-slate-500 mb-1">Units</div>
                    <Input type="number" inputMode="decimal" step="0.0001"
                      value={inp.units ?? ""}
                      onChange={(e) => setInput(r.id, { units: e.target.value })}
                      placeholder={r.units_held ? String(r.units_held) : "0"}
                      className="h-9 text-right font-mono-fin text-[13px] tabular-nums"
                      data-testid={`bn-units-${r.id}`} />
                  </div>
                  <div>
                    <div className="md:hidden text-[10px] uppercase tracking-wider text-slate-500 mb-1">New NAV (Rs)</div>
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px] text-slate-400 font-mono-fin pointer-events-none">₹</span>
                      <Input type="number" inputMode="decimal" step="0.0001"
                        value={inp.nav ?? ""}
                        onChange={(e) => setInput(r.id, { nav: e.target.value })}
                        placeholder={r.latest_nav ? String(r.latest_nav) : "0.0000"}
                        className="h-9 text-right font-mono-fin text-[13px] pl-6 tabular-nums focus:ring-2 focus:ring-red-500"
                        data-testid={`bn-nav-${r.id}`} />
                    </div>
                  </div>
                  <div className="md:text-right">
                    <div className="md:hidden text-[10px] uppercase tracking-wider text-slate-500 mb-1">Preview value</div>
                    <div className="font-mono-fin text-[13px] font-semibold text-slate-900 tabular-nums">
                      {preview > 0 ? formatRupees(preview) : <span className="text-slate-300">—</span>}
                    </div>
                    {preview > 0 && lastVal > 0 && (
                      <div className={`text-[10.5px] font-mono-fin tabular-nums ${delta >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                        {delta >= 0 ? "+" : ""}{formatRupeesCompact(delta)}
                      </div>
                    )}
                  </div>
                  <div className="md:text-right">
                    <div className="md:hidden text-[10px] uppercase tracking-wider text-slate-500 mb-1">Last update</div>
                    <div className="inline-flex flex-col md:items-end gap-0.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${stale.cls} w-fit`}>{stale.text}</span>
                      {r.last_snapshot_value && (
                        <span className="text-[10px] text-slate-400 font-mono-fin">{formatRupeesCompact(r.last_snapshot_value)}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sticky save bar */}
      <div className="sticky bottom-4 z-10 border border-slate-200 bg-white/95 backdrop-blur-md rounded-md p-3 sm:p-4 flex items-center justify-between gap-2 shadow-lg">
        <div className="text-[11.5px] sm:text-[12px] text-slate-600 min-w-0 flex-1">
          <div className="truncate">
            <span className="font-semibold text-slate-900 font-mono-fin tabular-nums">{preview.filledCount}</span>
            <span className="text-slate-400"> / {filtered.length}</span>
            <span className="hidden sm:inline"> ready — new total </span>
            <span className="sm:hidden"> · </span>
            <span className="font-semibold text-slate-900 font-mono-fin tabular-nums">{formatRupeesCompact(preview.filledValue)}</span>
          </div>
        </div>
        <Button onClick={save} disabled={saving || preview.filledCount === 0}
          className="rounded-md h-10 gap-1.5 bg-red-600 hover:bg-red-700 text-white disabled:opacity-40 shrink-0 px-3 sm:px-4"
          data-testid="bn-save">
          <Save className="h-4 w-4" />
          <span className="hidden sm:inline">{saving ? "Saving…" : `Save ${preview.filledCount || ""} NAV${preview.filledCount === 1 ? "" : "s"}`}</span>
          <span className="sm:hidden">{saving ? "…" : "Save"}</span>
        </Button>
      </div>
    </div>
  );
}
